import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { MenuItem } from '../types/myaccount';

interface MyAccountNavigationProps {
    menuItems?: MenuItem[];
    wrapInSidebar?: boolean;
    className?: string;
    onNavigate?: () => void;
}

const MyAccountNavigation: React.FC<MyAccountNavigationProps> = ({
    menuItems: initialMenuItems,
    wrapInSidebar = true,
    className = '',
    onNavigate,
}) => {
    const location = useLocation();

    const getDefaultMenuItems = (): MenuItem[] => [
        { id: 'dashboard', label: 'Dashboard', url: '/', icon: 'fas fa-tachometer-alt' },
        { id: 'orders', label: 'Orders', url: '/orders', icon: 'fas fa-shopping-bag' },
        { id: 'downloads', label: 'Downloads', url: '/downloads', icon: 'fas fa-download' },
        { id: 'addresses', label: 'Addresses', url: '/edit-address', icon: 'fas fa-map-marker-alt' },
        { id: 'account-details', label: 'Account Details', url: '/edit-account', icon: 'fas fa-user' },
        { id: 'logout', label: 'Logout', url: '/my-account/customer-logout', icon: 'fas fa-sign-out-alt' },
    ];

    const menuItems = useMemo(
        () => (Array.isArray(initialMenuItems) && initialMenuItems.length > 0 ? initialMenuItems : getDefaultMenuItems()),
        [initialMenuItems]
    );

    const normalizePath = (raw: string): string => {
        let path = String(raw || '').trim();
        if (!path) {
            return '/';
        }

        if (/^https?:\/\//i.test(path)) {
            try {
                path = new URL(path).pathname;
            } catch (_error) {
                // Fall back to raw value if URL parsing fails.
            }
        }

        if (!path.startsWith('/')) {
            path = `/${path}`;
        }

        if (path.startsWith('/my-account')) {
            path = path.substring('/my-account'.length) || '/';
            if (!path.startsWith('/')) {
                path = `/${path}`;
            }
        }

        if (path.length > 1 && path.endsWith('/')) {
            path = path.slice(0, -1);
        }

        return path || '/';
    };

    const isShoppingCartMenuItem = (item: MenuItem, normalizedItemPath: string): boolean => {
        if (item.id === 'shopping_cart_link') {
            return true;
        }

        if (normalizedItemPath === '/cart') {
            return true;
        }

        return Array.isArray(item.reactRoutes) && item.reactRoutes.some((route) => normalizePath(route.path) === '/cart');
    };

    const isActive = (item: MenuItem): boolean => {
        const currentPath = normalizePath(location.pathname);
        const itemPath = normalizePath(item.url);

        if (isShoppingCartMenuItem(item, itemPath)) {
            return currentPath === '/cart'
                || currentPath === '/checkout'
                || currentPath.startsWith('/order-received');
        }

        if (item.id === 'orders' || itemPath === '/orders') {
            if (currentPath.startsWith('/view-order/')) {
                return true;
            }
        }

        if (itemPath === '/' || itemPath === '') {
            return currentPath === '/';
        }

        return currentPath === itemPath || currentPath.startsWith(`${itemPath}/`);
    };

    const navigationMarkup = (
        <nav className={['woocommerce-MyAccount-navigation', className].filter(Boolean).join(' ')} aria-label="Account pages">
            <ul>
                {menuItems
                    .filter((item) => item.visible !== false)
                    .map((item) => {
                        const isCurrentPage = isActive(item);
                        const hasReactRoute = Array.isArray(item.reactRoutes) && item.reactRoutes.length > 0;
                        const isExternalLink = item.id === 'logout'
                            || item.id === 'customer-logout'
                            || item.url.startsWith('http')
                            || !hasReactRoute;
                        const listItemClassName = [
                            'woocommerce-MyAccount-navigation-link',
                            `woocommerce-MyAccount-navigation-link--${item.id}`,
                            isCurrentPage ? 'is-active' : '',
                        ].filter(Boolean).join(' ');
                        const linkContent = (
                            <>
                                {item.icon && (
                                    <i className={`${item.icon} weka-my-account-menu-icon`} aria-hidden="true"></i>
                                )}
                                <span className="weka-my-account-menu-label">{item.label}</span>
                            </>
                        );

                        return (
                            <li key={item.id} className={listItemClassName}>
                                {isExternalLink ? (
                                    <a
                                        href={item.url}
                                        className="weka-my-account-menu-link"
                                        aria-current={isCurrentPage ? 'page' : undefined}
                                        onClick={onNavigate}
                                    >
                                        {linkContent}
                                    </a>
                                ) : (
                                    <Link
                                        to={item.url}
                                        className="weka-my-account-menu-link"
                                        aria-current={isCurrentPage ? 'page' : undefined}
                                        onClick={onNavigate}
                                    >
                                        {linkContent}
                                    </Link>
                                )}
                            </li>
                        );
                    })}
            </ul>
        </nav>
    );

    if (!wrapInSidebar) {
        return navigationMarkup;
    }

    return (
        <div className="my-account-sidebar-wrapper">
            <div className="sidebar">
                {navigationMarkup}
            </div>
        </div>
    );
};

export default MyAccountNavigation;
